import { Plus, Trash2, Layers, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FIXED_FIELDS, CATEGORY_LABELS, CATEGORY_ORDER } from "./designerUtils";

const OVERLAY_KEY = { text: "ovText", image: "ovImage", logo: "ovLogo", upc: "ovUpc", cover: "ovCover" };

function Row({ active, onClick, label, onDelete, testId }) {
  return (
    <div
      onClick={onClick}
      data-testid={testId}
      className={`group flex items-center justify-between rounded-md px-2.5 py-2 text-sm cursor-pointer transition-colors ${
        active ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-600 hover:bg-slate-50"
      }`}
    >
      <span className="truncate">{label}</span>
      {onDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default function ElementsList({ mode, template, customContent, selection, setSelection, onAddText, onAddImage, onAddOverlay, onRemoveText, onRemoveImage, onRemoveOverlay, onAutoDetectText, detectingText, drawingLogo, onToggleDrawLogo }) {
  const { t } = useTranslation();
  const isSel = (kind, key) => selection && selection.kind === kind && selection.key === key;

  return (
    <div className="flex h-full w-56 shrink-0 flex-col border-r border-slate-200 bg-white" data-testid="elements-panel">
      <div className="flex items-center gap-1.5 border-b border-slate-100 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
        <Layers className="h-3.5 w-3.5" /> {t("elements.title")}
      </div>
      <div className="flex-1 overflow-y-auto bhf-scrollbar p-2 space-y-0.5">
        <Row active={isSel("background")} onClick={() => setSelection({ kind: "background" })} label={t("elements.background")} testId="element-background" />

        {mode === "A" && FIXED_FIELDS.filter((f) => template?.elementConfig?.[f.key]).map((f) => (
          <Row key={f.key} active={isSel("fixed", f.key)} onClick={() => setSelection({ kind: "fixed", key: f.key })} label={f.label} testId={`element-${f.key}`} />
        ))}

        {mode === "A" && (customContent.addedTexts || []).map((tx, i) => (
          <Row key={tx.id || i} active={isSel("text", i)} onClick={() => setSelection({ kind: "text", key: i })} label={t("elements.addedText", { n: i + 1 })} onDelete={() => onRemoveText(i)} testId={`element-added-text-${i}`} />
        ))}
        {mode === "A" && (customContent.addedImages || []).map((im, i) => (
          <Row key={im.id || i} active={isSel("image", i)} onClick={() => setSelection({ kind: "image", key: i })} label={t("elements.addedImage", { n: i + 1 })} onDelete={() => onRemoveImage(i)} testId={`element-added-image-${i}`} />
        ))}

        {mode === "B" && (() => {
          const overlays = customContent.overlays || [];
          const groups = {};
          overlays.forEach((o, i) => {
            const cat = o.category || "other";
            (groups[cat] = groups[cat] || []).push(i);
          });
          return CATEGORY_ORDER.filter((cat) => groups[cat]?.length).map((cat) => (
            <div key={cat} data-testid={`element-group-${cat}`}>
              <div className="px-2 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-400">{CATEGORY_LABELS[cat]}</div>
              {groups[cat].map((i) => {
                const o = overlays[i];
                return (
                  <Row key={o.id || i} active={isSel("overlay", i)} onClick={() => setSelection({ kind: "overlay", key: i })}
                    label={`${t(`elements.${OVERLAY_KEY[o.type] || "ovText"}`)} ${i + 1}`} onDelete={() => onRemoveOverlay(i)} testId={`element-overlay-${i}`} />
                );
              })}
            </div>
          ));
        })()}
      </div>

      <div className="space-y-1.5 border-t border-slate-100 p-2">
        {mode === "A" ? (
          <>
            <button onClick={onAddText} data-testid="add-text-button" className="flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-200 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
              <Plus className="h-3.5 w-3.5" /> {t("elements.addText")}
            </button>
            <button onClick={onAddImage} data-testid="add-image-button" className="flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-200 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
              <Plus className="h-3.5 w-3.5" /> {t("elements.addImage")}
            </button>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-1.5">
            <button
              onClick={onAutoDetectText}
              disabled={detectingText}
              data-testid="auto-detect-text-button"
              className="col-span-2 flex items-center justify-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 py-2 text-xs font-medium text-indigo-600 hover:bg-indigo-100 disabled:opacity-50"
            >
              <Sparkles className="h-3.5 w-3.5" /> {detectingText ? t("elements.aiDetecting") : t("elements.aiDetect")}
            </button>
            {["logo", "text", "image", "upc", "cover"].map((ty) =>
              ty === "logo" ? (
                <button
                  key={ty}
                  onClick={onToggleDrawLogo}
                  data-testid="add-overlay-logo"
                  className={`flex items-center justify-center gap-1 rounded-md border py-1.5 text-[11px] font-medium ${
                    drawingLogo ? "border-indigo-400 bg-indigo-100 text-indigo-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Plus className="h-3 w-3" /> {drawingLogo ? t("elements.drawing") : t("elements.drawLogo")}
                </button>
              ) : (
                <button key={ty} onClick={() => onAddOverlay(ty)} data-testid={`add-overlay-${ty}`} className="flex items-center justify-center gap-1 rounded-md border border-slate-200 py-1.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50">
                  <Plus className="h-3 w-3" /> {t(`elements.${OVERLAY_KEY[ty]}`)}
                </button>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
